import { html, TemplateResult } from "lit";
import { property, state} from "lit/decorators.js";
import { nothing } from 'lit-html';
import { connect } from 'pwa-helpers';
import store from '../../store/store';
import "../alert/alert";
import { BaseComponent } from "../../features/base-component/base-component";
import { booleanConverter } from "../../common/converters";
import { MessageType } from "../alert/global-message.interface";
import { loading, smallSpinner } from "../../common/constants/api-constants";
import { isCheckoutComplete, isCheckoutInfoSubmitted, updateActiveStep } from "./shop-checkout";
import { StoreState } from "../../store/store.interface";
import emit from "../../common/events";
import { ApiError } from "../../common/utils/api-utils";
import { scrollTopInDesktop } from "../../common/utils/util";
import _ from "lodash";
import { waitForElementsToLoad } from "../../common/utils/delay-util";

/**
 * Template to be implemented by every checkout step.
 */
export interface CheckoutStepTemplate {
  /**
   * Render the part of the checkout step which is taking user input
   */
   renderCheckoutStep(): TemplateResult;
  /**
   * Render the results of the checkout step
   */
   renderCompletedStepResults(): TemplateResult;
  /**
   * Method to load checkout step
   */
   loadStep(): void;
  /**
   * Method to validate checkout step
   */
   stepDoValidation(): Promise<void>;
  /**
   * Method to be executed when checkout step is edited.
   */
   editStepAction(): Promise<void>;
  /**
   * Method to Force complete a checkout step.
   */
  forceCompleteStep(): Promise<any>;
}

/**
 *  Wrapper parent element of a concrete checkout step.
 */
export class CheckoutStep extends connect(store)(BaseComponent) {
  @property()
  stepName = "Step Name";

  @property()
  nextStepButtonName = "Next Step";

  @property()
  editStepButtonName = "Edit";

  @property({converter: booleanConverter})
  showSequenceOrder = false;

  @property({type: Number})
  activeStepValue = 1;

  /**
   * Error message to be displayed.
   */
  @state()
  protected errorMessage?: string;

  /**
   * Braintree Hosted Fields Error message to be displayed.
   */
  @state()
  protected braintreeHostedFieldsErrorMessage?: string;

  /**
   * Sequence order of execution of the checkout step
   */
  @property({type: Number})
  order = 1;

  /**
   * Attribute to show if user is actively entering info in this checkout step
   */
  @property({ converter: booleanConverter, reflect: true })
  isActive = false;


  @property({converter: booleanConverter, reflect: true })
  forceComplete = false;

  /**
   * Flag to indicate whether checkout step is editable.
   */
  @state()
  protected canEdit = false;

  /**
   * Attribute to show if user has completed the checkout step
   */
  @state()
  private isComplete = false;

  /**
   * Flag to indicate whether checkout step is loading.
   */
  @state()
  protected isLoading = true;

  /**
   * Attribute to disable the next step button
   */
  @state()
  protected isNextStepButtonDisabled = false;

  /**
   * Flag to indicate whether the next step is calculating, to show the spinner and disable the button.
   */
  @state()
  protected isCalculatingNextStep = false;

  connectedCallback(): void {
    super.connectedCallback();
    this.addEventListener(`${this.id}-checkout-step-complete`, this.onClickNextStepButton);
    this.addEventListener('form-submit-error', this.setPageError.bind(this));
    this.addEventListener('form-submit-clear-error', this.clearPageError.bind(this));
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.removeEventListener(`${this.id}-checkout-step-complete`, this.onClickNextStepButton);
    this.removeEventListener('form-submit-error', this.setPageError.bind(this));
    this.removeEventListener('form-submit-clear-error', this.clearPageError.bind(this));
  }
  /**
   * If the order of the checkout step is 1, it is set as active when it loads.
   */
  async firstUpdated(_changedProperties: Map<string | number | symbol, unknown>) {
    super.firstUpdated(_changedProperties);
    if(this.order === 1){
      this.loadCheckoutStep();
    }
  }

  /**
   * Checks if the step can be edited, this can happen in the following scenarios:
   *
   * 1. If the checkout info has been submitted
   * 2. If the checkout has been forced completed and all the steps were properly loaded
   * 3. If the step is complete and the order of the step is inferior or equal to the active step
   */
  protected canEditStep(state: StoreState): void {
    if (isCheckoutInfoSubmitted(state)) {
      this.canEdit = true;
      return;
    }

    if (this.forceComplete) {
      this.canEdit = isCheckoutComplete(state);
      return;
    }

    this.canEdit = this.isComplete && this.order <= this.activeStepValue;
  }

  /**
   * Renders the concrete implementation checkout step template in the common wrapper template of the checkout step
   * 
   * @returns 
   */
  renderComp() {
    return html`
      <section class="mb-3">
        ${this.isActive ? this.renderActiveTemplate(): this.renderInactiveTemplate()}
      </section>
    `;
  }

  private setPageError(event) {
    if(event?.detail?.error) {
      this.errorMessage = event.detail.error;
    }
  }
  
  private clearPageError() {
    this.errorMessage = "";
  }

  private renderActiveTemplate(){
    return html`
      <div class="text-white pt-3 pb-2 px-3 bg-secondary">
        <h2 class="h4">${this.showSequenceOrder? html`${this.order}.&nbsp;`: nothing }${this.stepName}</h2>
      </div>
      ${this.errorMessage ? html`
        <div class="my-3">
          <mhecomm-alert type=${MessageType.danger} message=${this.errorMessage}></mhecomm-alert>
        </div>
      `: nothing}
      <div class="m-3">
        ${this.isLoading ? this.renderLoadingTemplate(): this.renderCheckoutStep()}
      </div>
      `;
  }

  renderNextStepButton(){
    return html`
      <div>
        ${this.isLoading ? nothing : html `
          <button
            id="btn-${this.id}"
            type="button"
            ?disabled="${!this.isActive || this.isCalculatingNextStep || this.isNextStepButtonDisabled}"
            class="btn btn-primary btn-lg mt-4 mb-2"
            @click="${this.onClickNextStepButton}">
            ${this.nextStepButtonName}
            ${ this.isCalculatingNextStep ? smallSpinner() : ''}
          </button>
        `}
      </div>
    `;
  }

  renderLoadingTemplate(){
    return html`
      <span class="visually-hidden">Loading checkout step</span>
      ${loading()}
      ${loading({ columns: 2 })}
      ${loading()}
      ${loading()}
    `;
  }
  
  renderCheckoutStep(): unknown {
    throw new Error('Method not implemented.');
  }

  private renderInactiveTemplate(){
    return html`
      <div class="d-flex bg-light pt-3 pb-2 px-3">
        <h2 class="h4 flex-grow-1">${this.showSequenceOrder? html`${this.order}.&nbsp;`: nothing }${this.stepName}</h2>
        ${this.canEdit ? html`
          <div>
            <button id="edit-${this.id}" @click="${this.onClickEditStepButton}" class="btn btn-link p-0" aria-label=${"edit " + this.stepName}>
              ${this.editStepButtonName}
            </button>
          </div>
        ` : nothing}
      </div>
      ${this.errorMessage ? html`
        <div class="my-3">
          <mhecomm-alert type=${MessageType.danger} message=${this.errorMessage}></mhecomm-alert>
        </div>
      `: nothing}
      ${this.isLoading && this.forceComplete ? this.renderLoadingTemplate() : this.isComplete? this.renderCompletedStepTemplate() : nothing }`;
  }

  private renderCompletedStepTemplate(){
    return html`
      <div class="mt-3 p-3">
        <div>${this.renderCompletedStepResults()}</div>
      </div>`;
  }
  
  renderCompletedStepResults() {
    throw new Error('Method not implemented.');
  }

  /**
   * Method to load checkout step
   */
  public loadCheckoutStep(){
    if(this.forceComplete){
      this.isComplete = true;
      this.forceCompleteStep().then(() =>{
        emit({
          element: this,
          name: 'mhecomm-checkout-step-force-complete',
          detail: {id: this.id},
        });
      }).catch((error: Error) => {
        if(error instanceof ApiError){
          console.error(`Failed to force complete ${this.stepName}`,error);
        }
        else {
          this.errorMessage = error.message;
        }
        emit({
          element: this,
          name: 'mhecomm-checkout-step-force-complete-failed',
          detail: {id: this.id},
        });
      });
    } else {
      this.isActive = true;
      this.isComplete = false;
      store.dispatch(updateActiveStep(this.id));
      this.loadStep();
    }
    
  }

  public editCheckoutStep(){
    this.isActive = true;
    this.isComplete = false;
    this.errorMessage = undefined;
    this.braintreeHostedFieldsErrorMessage = undefined;
    this.editStepAction();
  }

  updated(changedProperties: any) {
    if (changedProperties.has('isActive') && this.isActive) {
      this.scrollTo();
    }
  }
  
  loadStep() {
    throw new Error('Method not implemented.');
  }

  /**
   * On click edit step button in checkout step,
   * dispatch step edited event to parent checkout steps component.
   * @param e 
   */
  private onClickEditStepButton(e: Event){
    e.preventDefault();
    emit({
      element: this,
      name: 'mhecomm-checkout-step-edited',
      detail: {id: this.id},
    });
  }
  
  async editStepAction() {
    throw new Error('Method not implemented.');
  }

  async forceCompleteStep() {
    throw new Error('Method not implemented.');
  }

  /**
   * On click next step button, validate step.
   * If invalid call failure action.
   * @param e 
   */
  private onClickNextStepButton(_e: CustomEvent){
    this.isCalculatingNextStep = true;
    this.errorMessage = '';
    this.braintreeHostedFieldsErrorMessage = undefined;
    this.stepDoValidation()
    .then(() => {
      if(!this.errorMessage && !this.braintreeHostedFieldsErrorMessage){
        this.isComplete = true;
        // Fire event to parent checkout steps component to trigger next step.
        emit({
          element: this,
          name: 'mhecomm-checkout-step-complete',
          detail: this.checkoutStepCompleteDetails(),
        });
      }
    })
    .catch((error: Error) => {
      if(error instanceof ApiError){
        console.error(`Failed to validate ${this.stepName}`,error);
      }
      else {
        this.errorMessage = error.message;
      }
    })
    .finally(() => {
      this.isCalculatingNextStep = false;
      this.scrollTo();
      scrollTopInDesktop();

      // this peice of code will focus on edit button only if the event bubble up from paypal button element.
      if(_e?.detail?.comp === "mhecomm-paypal-payment-method") {
        const editButton = `#edit-${this.id}`;
        waitForElementsToLoad(this.shadowRoot!, [editButton]).then(() => {
          (this.shadowRoot?.querySelector(editButton) as HTMLButtonElement)?.focus();
        })
      }
    });
  }

  checkoutStepCompleteDetails(): any {
    throw new Error('Method not implemented.');
  }

  stepDoValidation(): Promise<void> {
    throw new Error('Method not implemented.');
  }

  handleApiFailures(_state: StoreState){
    throw new Error('Method not implemented.');
  }

  public setActive(active: boolean){
    this.isActive = active;
  }

  public getOrder(){
    return this.order;
  }

  public hasCompleted(){
    return this.isComplete;
  }

  public forceIncomplete(){
    this.isComplete = false;
  }
}
